import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, retry, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { NotificationDismissed, SensorEvents } from '../models/sensorEvents';
import { AuthService } from './auth.service';

export interface sumCustomStats { sensor: string, parking_events: number, occupancy: number };
@Injectable({ providedIn: 'root' })
export class SensorEventsService {

  private base_url = "";

  private customStats$: BehaviorSubject<any[]> = new BehaviorSubject([])
  public stats$: BehaviorSubject<any[]> = new BehaviorSubject([])
  private customSumStats$: BehaviorSubject<sumCustomStats[]> = new BehaviorSubject([{ sensor: null, parking_events: 0, occupancy: 0 }])
  public sumStats$: BehaviorSubject<sumCustomStats[]> = new BehaviorSubject([{ sensor: null, parking_events: 0, occupancy: 0 }])

  constructor(private http: HttpClient, private authService: AuthService) {
    this.base_url = environment.base_url;
  }

  setCustomStats(data: any[]) {
    this.customStats$.next(data)
    this.stats$.next(data)
  }

  setSumCustomStats(sumData: sumCustomStats[]) {
    console.log("SUMDATA", sumData)
    this.customSumStats$.next(sumData)
    this.sumStats$.next(sumData)
  }

  getStats(): Observable<any[]> {
    return this.stats$.asObservable();
  }

  getSumStats(): Observable<sumCustomStats[]> {
    return this.sumStats$.asObservable();
  }
  
  clearSumStats() {
    return this.sumStats$.next(null)
  }

  getCustomSensorStats(sensorID: number, filters: Filters) {

    let url = `${this.base_url}/api/customsensorstat`
    let flts: any;
    if (filters.starts_after !== null || filters.ends_before !== null) {
      flts = `&starts_after=${filters.starts_after}&ends_before=${filters.ends_before}`
    } else {
      flts = ``
    }

    let finalUrl = ''
    if (sensorID !== null) {
      finalUrl = `?sensor=${sensorID}${flts}`
    } else {
      finalUrl = ``
    }
    return this.http.get<any[]>(`${url}${finalUrl}`)
      .pipe(
        retry(2),
        map(result => {
          return result['data']
        }),
        catchError(err => {
          return throwError(err)
        })
      );
  }

  getCustomSumSensorStats(sensorID: number, filters: Filters) {
    let url = `${this.base_url}/api/sumcustomsensorstat`
    let flts: any;
    if (filters.starts_after !== null || filters.ends_before !== null) {
      flts = `&starts_after=${filters.starts_after}&ends_before=${filters.ends_before}`
    } else {
      flts = ``
    }

    let finalUrl = ''
    if (sensorID !== null) {
      finalUrl = `?sensor=${sensorID}${flts}`
    } else {
      finalUrl = ``
    }
    return this.http.get<sumCustomStats[]>(`${url}${finalUrl}`)
      .pipe(
        retry(2),
        map(result => {
          let array: any[] = [];
          array.push(result)
          return array
        }),
        catchError(err => {
          return throwError(err)
        })
      );
  }

  /**
   *
   * @param filters String
   * @description The filters must be of the format YYYY-MM-DD HH:MM[:ss[.uuuuuu]][TZ] format.']
   * @description > /api/occupancy/starts_after=2020-09-[currentDay]
   * @description > /api/occupancy/starts_after=2020-09-01
   * @description > /api/occupancy/starts_after=2020-01-01
   * @description HH:MM[:ss[.uuuuuu]][TZ] is for future requirement
   */
  getParkingEvts(filters: string) {
    let url = `${this.base_url}/api/occupancy`
    return this.http.get<any[]>(`${url}?starts_after=${filters}`)
  }

  /**
   *
   * @param sensorID Number
   * @description Get the events on sensor level
   * @description "local_arrival": "2020-09-04T12:27:25+03:00",
     "local_departure": "2020-09-04T12:37:54+03:00",
     "arrival": "2020-09-04T09:27:25Z",
     "departure": "2020-09-04T09:37:54Z"

     @returns Array<SensorEvents[]> of sensor events
   */
  getSensorEvents(sensorID: number) {
    let url = `${this.base_url}/api/events`
    return this.http.get<SensorEvents[]>(`${url}?sensor=${sensorID}`)
  }

  /**
   *
   * @param filters <Interface> Filters
   * @description The filters must be of the format YYYY-MM-DD HH:MM[:ss[.uuuuuu]][TZ] format.']
   * @description > /api/occupancy/starts_after=2020-09-[currentDay]
   * @description > /api/occupancy/ends_before=2020-09-[endDate]
   * @description HH:MM[:ss[.uuuuuu]][TZ] is for future requirement
   */
  getSensorEventsRangeData(sensorID: number, filters?: Filters) {
    let url = `${this.base_url}/api/events`
    let flts: any;
    if (filters.starts_after !== null || filters.ends_before !== null) {
      flts = `&starts_after=${filters.starts_after}&ends_before=${filters.ends_before}`
    } else {
      flts = ``
    }
    return this.http.get<SensorEvents[]>(`${url}?sensor=${sensorID}${flts}`)
      .pipe(
        retry(2),
        map(result => {
          return result
        }),
        catchError(err => {
          return throwError(err)
        })
      );
  }

  /**
   *
   * @param filters <Interface> Filters
   * @description The filters must be of the format YYYY-MM-DD HH:MM[:ss[.uuuuuu]][TZ] format.']
   * @description > /api/occupancy/starts_after=2020-09-[currentDay]
   * @description > /api/occupancy/ends_before=2020-09-[endDate]
   * @description HH:MM[:ss[.uuuuuu]][TZ] is for future requirement
   */
  getEventsAtSensorLevel(sensorID?: number, filters?: Filters, last?: boolean) {
    let url = `${this.base_url}/api/sensorstat`
    let flts: any;
    if (filters.starts_after !== null || filters.ends_before !== null) {
      if (filters.starts_after !== null && filters.ends_before == null) {
        flts = last && last !== undefined ? `?starts_after=${filters.starts_after}&last=1` : `?starts_after=${filters.starts_after}`
      }
      if (filters.starts_after == null && filters.ends_before !== null) {
        flts = `?ends_before=${filters.ends_before}`
      }
      if (filters.starts_after !== null && filters.ends_before !== null) {
        flts = `?starts_after=${filters.starts_after}&ends_before=${filters.ends_before}`
      }

    } else {
      flts = ``
    }

    let finalUrl = ''
    if (sensorID !== null) {
      finalUrl = `?sensor=${sensorID}${flts}`
    } else {
      finalUrl = `${flts}`
    }
    return this.http.get<any[]>(`${url}${finalUrl}`)
      .pipe(
        retry(2),
        map(result => {
          return result['data']
        }),
        catchError(err => {
          return throwError(err)
        })
      );
  }




  getSensorHourlyEvents(sensorSerial?: any, filters?: Filters, last?: boolean) {
    let url = `${this.base_url}/api/sensorhourlystats`
    let flts: any;
    if (filters.starts_after !== null || filters.ends_before !== null) {
      if (filters.starts_after !== null && filters.ends_before == null) {
        flts = last && last !== undefined ? `&after=${filters.starts_after}&last=1` : `&after=${filters.starts_after}`
      }
      if (filters.starts_after == null && filters.ends_before !== null) {
        flts = `&before=${filters.ends_before}`
      }
      if (filters.starts_after !== null && filters.ends_before !== null) {
        flts = `&after=${filters.starts_after}&before=${filters.ends_before}`
      }

    } else {
      flts = ``
    }

    let finalUrl = ''
    if (sensorSerial !== null || sensorSerial !== undefined) {
      finalUrl = `?sensor=${sensorSerial}${flts}`
    } else {
      finalUrl = `${flts}`
    }
    console.log("final", finalUrl)
    return this.http.get<any[]>(`${url}${finalUrl}`)
      .pipe(
        retry(2),
        map(result => {
          console.log("result", result)
          return result['data']
        }),
        catchError(err => {
          return throwError(err)
        })
      );
  }


  getSensorWeeklyEvents(sensorSerial?: any, filters?: NewFilters, last?: boolean) {
    let url = `${this.base_url}/api/sensorweeklystats`
    let flts: any;
    if (filters.after !== null || filters.before !== null) {
      if (filters.after !== null && filters.before == null) {
        flts = last && last !== undefined ? `?after=${filters.after}&last=1` : `?after=${filters.after}`
      }
      if (filters.after == null && filters.before !== null) {
        flts = `?before=${filters.before}`
      }
      if (filters.after !== null && filters.before !== null) {
        flts = `?after=${filters.after}&ends_before=${filters.before}`
      }

    } else {
      flts = ``
    }

    let finalUrl = ''
    if (sensorSerial !== null || sensorSerial !== undefined) {
      finalUrl = `?sensor=${sensorSerial}${flts}`
    } else {
      finalUrl = `${flts}`
    }
    console.log("final", finalUrl)
    return this.http.get<any[]>(`${url}${finalUrl}`)
      .pipe(
        retry(2),
        map(result => {
          console.log("result", result)
          return result['data']
        }),
        catchError(err => {
          return throwError(err)
        })
      );
  }

  getSensorMonthlyEvents(sensorSerial?: any, filters?: NewFilters, last?: boolean) {
    let url = `${this.base_url}/api/sensormonthlystats`
    let flts: any;
    if (filters.after !== null || filters.before !== null) {
      if (filters.after !== null && filters.before == null) {
        flts = last && last !== undefined ? `?after=${filters.after}&last=1` : `?after=${filters.after}`
      }
      if (filters.after == null && filters.before !== null) {
        flts = `?before=${filters.before}`
      }
      if (filters.after !== null && filters.before !== null) {
        flts = `?after=${filters.after}&ends_before=${filters.before}`
      }

    } else {
      flts = ``
    }

    let finalUrl = ''
    if (sensorSerial !== null || sensorSerial !== undefined) {
      finalUrl = `?sensor=${sensorSerial}${flts}`
    } else {
      finalUrl = `${flts}`
    }
    console.log("final", finalUrl)
    return this.http.get<any[]>(`${url}${finalUrl}`)
      .pipe(
        retry(2),
        map(result => {
          console.log("result", result)
          return result['data']
        }),
        catchError(err => {
          return throwError(err)
        })
      );
  }

  getSensorNotifications() {
    let url = `${this.base_url}/api/message`
    return this.http.get<any[]>(`${url}`)
      .pipe(
        retry(2),
        map(result => {
          return result['data']
        }),
        catchError(err => {
          return throwError(err)
        })
      )
  }

  getDismissedOrNotSensorNotifications({ dismissed = undefined, id = undefined }: { dismissed?: undefined | boolean, id?: undefined | number }) {
    let url = id !== undefined ? `${this.base_url}/api/message?sensor=${id}` : `${this.base_url}/api/message`
    return this.http.get<any[]>(`${url}`)
      .pipe(
        retry(2),
        map(result => {
          if (result['data']) {
            switch (dismissed) {
              case (true || undefined || null):
                result['data'] = result['data'].filter(item => {
                  if (this.authService.isLoggedInMunicipalPolice) {
                    if (item['attributes']['dismissed'] === true && (item['attributes']['type'] === 'overtime' || item['attributes']['type'] === 'violation')) return item
                  } else {
                    if (item['attributes']['dismissed'] === true) return item
                  }
                })
                return result['data']
              case (false):
                result['data'] = result['data'].filter(item => {
                  if (this.authService.isLoggedInMunicipalPolice) {
                    if (item['attributes']['dismissed'] === false && (item['attributes']['type'] === 'overtime' || item['attributes']['type'] === 'violation')) return item
                  } else {
                    if (item['attributes']['dismissed'] === false) return item
                  }
                })
                return result['data']
              default:
                return result['data']
            }
          } else return []
        }),
        shareReplay(1),
        catchError(err => {
          return throwError(err)
        })
      )
  }

  /**
   *
   * @param notificationDismissed <Interface> NotificationDismissed
   */
  patchSensorNotifications(notificationDismissed: NotificationDismissed) {
    let url = `${this.base_url}/api/message/${Number(notificationDismissed.data.id)}`
    return this.http.patch(`${url}`, notificationDismissed)
      .pipe(
        map(result => {
          return result['data']
        }),
        catchError(err => {
          return throwError(err)
        })
      )
  }
}

export interface Filters {
  sensorID?: number;
  starts_after?: string;
  ends_before?: string;
}
export interface NewFilters {
  sensorSerial?: any;
  after?: string;
  before?: string;
}
